/**
 * Inbox message thread and composer.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Divider } from "@mui/material";

import VButton from "form/VButton";
import VText from "form/VText";
import VAvatar from "form/VAvatar";
import VFroala from "form/VFroala";
import {
  actionChatAdd,
  actionChatList,
  actionChatMarkAllRead,
  actionChatMoreList,
  actionChatTyping,
} from "context/action";
import { useAsterController } from "context";
import { getDateObj, getShortTime } from "utils/date";
import SafeHtml from "pages/common/SafeHtml";
import { PT, asArray, asBool, asNumber, asObject, asString } from "utils/safeData";
import ChatTypingDots, { htmlHasText } from "./chat.typing";

/**
 * Converts a message timestamp into minutes since epoch for grouping.
 *
 * @param {string|number} value Created-at value.
 * @returns {number}
 */
const minutesFromCreated = (value) => {
  const date = getDateObj(value);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.getTime() / (1000 * 60) : 0;
};

/**
 * Renders chat history, load-more, and the Froala composer.
 *
 * @param {{ height?: number }} props
 */
const ChatContent = (props) => {
  const height = asNumber(props.height, 500);

  const [controller, dispatch] = useAsterController();
  const user = asObject(controller?.user);
  const admin = asObject(controller?.admin);
  const chatList = asObject(controller?.chatList);
  const chatsMoreLoaded = asBool(controller?.chats_more_loaded, true);
  const isChatEnabled = asBool(controller?.isChatEnabled, true);
  const chatTyping = asObject(controller?.chatTyping);
  const displayName =
    `${asString(admin.first_name) || asString(admin.username)} ${asString(admin.last_name)}`.trim() || "(Unknown)";

  const [model, setModel] = useState("");
  const [messages, setMessages] = useState([]);

  const chatScrollRef = useRef(null);
  const markedReadForCurrentBatchRef = useRef(false);

  /**
   * Scrolls the thread to the latest message.
   *
   * @returns {void}
   */
  const scrollUp = () =>
    setTimeout(() => {
      chatScrollRef.current?.scroll({ top: 0, behavior: "smooth" });
    }, 200);

  useEffect(() => {
    if (!controller.sessionHydrated) return;
    if (user.id == null || user.id === "") return;
    actionChatList(dispatch, { user_id: user.id, page: 0, page_size: 20 });
  }, [controller.sessionHydrated, dispatch, user.id]);

  useEffect(() => {
    const incoming = asArray(chatList.messages).map((msg) => asObject(msg));
    if (incoming.length) {
      const next = incoming;

      for (let i = 1; i < next.length; i++) {
        next[i - 1].show_date = next[i].to_admin !== next[i - 1].to_admin;

        const currMins = minutesFromCreated(next[i - 1].created);
        const prevMins = minutesFromCreated(next[i].created);

        next[i - 1].show_date =
          !next[i - 1].show_date && currMins - prevMins > 10 ? true : next[i - 1].show_date;
      }

      next[next.length - 1].show_date = true;

      setMessages(next);
      markedReadForCurrentBatchRef.current = false;

      if (next.find((v) => v.is_new)) scrollUp();
    } else {
      setMessages([]);
      markedReadForCurrentBatchRef.current = false;
    }
  }, [chatList, dispatch]);

  useEffect(() => {
    if (!user.id) return undefined;
    const eventId = `${process.env.REACT_APP_SITE_NAME}_${user.id}`;
    const typing = htmlHasText(model);
    let startTimer;
    let stopTimer;
    if (typing && isChatEnabled) {
      startTimer = window.setTimeout(() => actionChatTyping(dispatch, eventId, true), 250);
      stopTimer = window.setTimeout(() => actionChatTyping(dispatch, eventId, false), 1800);
    } else {
      actionChatTyping(dispatch, eventId, false);
    }
    return () => {
      window.clearTimeout(startTimer);
      window.clearTimeout(stopTimer);
    };
  }, [model, user.id, isChatEnabled, dispatch]);

  const showTyping =
    asBool(chatTyping.typing) &&
    !chatTyping.room &&
    Number(chatTyping.from_id) !== Number(user.id);

  /**
   * Marks inbox messages read when the thread is scrolled to the visual bottom.
   *
   * @returns {void}
   */
  const maybeMarkAllRead = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;

    const thresholdPx = 24;
    const isAtVisualBottom = el.scrollTop <= thresholdPx;
    const hasUnread = asArray(messages).some((m) => asObject(m).is_new);

    if (isAtVisualBottom && hasUnread && !markedReadForCurrentBatchRef.current) {
      markedReadForCurrentBatchRef.current = true;
      actionChatMarkAllRead(dispatch);
    }
  }, [dispatch, messages]);

  useEffect(() => {
    maybeMarkAllRead();
  }, [maybeMarkAllRead]);

  /**
   * Sends a chat message through the store.
   *
   * @param {string} value Sanitized HTML body.
   * @returns {void}
   */
  const onSubmit = (value) => {
    const currTime = new Date().toISOString();
    actionChatAdd(dispatch, {
      user_id: user.id,
      text: asString(value),
      to_admin: true,
      created: currTime,
      updated: currTime,
    });
    actionChatTyping(dispatch, `${process.env.REACT_APP_SITE_NAME}_${user.id}`, false);
    scrollUp();
  };

  /**
   * Fetches the next page of older messages.
   *
   * @returns {void}
   */
  const onLoadMore = () =>
    asBool(chatList.message_more) &&
    actionChatMoreList(dispatch, { user_id: user.id, page: asNumber(chatList.page) + 1, page_size: 20 });

  return (
    <Box className="relative w-full border-0 border-t border-solid border-gray-300 dark:border-gray-500 v-chat-content">
      <Box className="p-6 hidden md:flex items-center h-[88px]">
        <VText color="secondary" className="text-2xl">
          @
        </VText>
        &nbsp;&nbsp;
        <VText className="text-2xl text-limit-2">{displayName}</VText>
      </Box>

      <Divider className="bg-gray-200 dark:bg-gray-500" />

      <Box height={height} className="flex flex-col">
        <Box
          ref={chatScrollRef}
          onScroll={maybeMarkAllRead}
          className="flex-1 w-full overflow-y-auto v-light-scrollbar px-3 pb-3 flex flex-col-reverse"
          sx={{ maxHeight: Math.max(height - 130, 0) }}
          id="v-chat-content"
        >
          {showTyping ? <ChatTypingDots /> : null}
          {asArray(messages).map((rawMsg) => {
            const msg = asObject(rawMsg);
            return !msg.to_admin ? (
              <Box key={msg.created} className={`flex w-3/4 ${msg.show_date ? "mt-3" : "mt-1"}`}>
                <VAvatar
                  src={asString(admin.avatar)}
                  bgColor="light"
                  className={`w-10 h-10 ${msg.show_date ? "visible" : "invisible"}`}
                />
                <Box className="ml-3">
                  <VText color="secondary" className={`text-sm ${msg.show_date ? "" : "hidden"}`}>
                    {displayName} {asString(admin.last_name)}, {getShortTime(msg.created)}
                  </VText>
                  <Box className="v-chat-bubble v-chat-bubble--recv">
                    <VText color="custom">
                      <SafeHtml html={asString(msg.text)} />
                    </VText>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                key={msg.created}
                className={`flex flex-col w-3/4 items-end ml-auto ${msg.show_date ? "mt-3" : "mt-1"}`}
              >
                <VText color="secondary" className={`text-sm ${msg.show_date ? "" : "hidden"}`}>
                  You, {getShortTime(msg.created)}
                </VText>
                <Box className="v-chat-bubble v-chat-bubble--sent">
                  <VText color="custom">
                    <SafeHtml html={asString(msg.text)} />
                  </VText>
                </Box>
              </Box>
            );
          })}

          <Box className="flex justify-center mb-auto">
            <VButton
              variant="text"
              color="secondary"
              size="small"
              onClick={onLoadMore}
              loading={!chatsMoreLoaded}
              disabled={!asBool(chatList.message_more)}
            >
              Load more ...
            </VButton>
          </Box>
        </Box>

        <Divider className="bg-gray-200 dark:bg-gray-700" />

        <Box className="v-chat-froala relative v-chat-dark">
          <VFroala
            isChat
            isEnterSubmit
            onSubmit={onSubmit}
            model={asString(model)}
            setModel={setModel}
            disabled={!isChatEnabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

ChatContent.defaultProps = {
  height: 500,
};

ChatContent.propTypes = {
  height: PT.height,
};

export default ChatContent;
